'use client';

import { useEffect, useMemo, useState, type ReactNode } from 'react';

import {
  Box,
  Flex,
  HStack,
  PopoverBody,
  Separator,
  Span,
  type StackProps,
  DateValue,
} from '@chakra-ui/react';
import { Close, KeyboardArrowDown, KeyboardArrowUp } from '@m/icons';
import { format, isSameDay, startOfDay } from 'date-fns';

import { Button } from '@m/components-ui/button';
import { PopoverContent, PopoverRoot, PopoverTrigger } from '@m/components-ui/popover';

import { dateToDateValue, dateValueToDate } from './date-picker';
import { DatePickerMenu } from './date-picker-menu';
import { DatePickerSheet } from './date-picker-sheet';

import type { DatePickerSelectionMode } from './date-picker-menu';

export type DateFilterPillProps = {
  /**
   * When true, the filter pill is non-interactive (e.g. no upcoming events to
   * filter).
   */
  disabled?: boolean;
  /** Callback when a single date is selected or cleared (`selectionMode="single"`). */
  onDateChange?: (date: Date | null) => void;
  /**
   * Callback when a date range is selected or cleared (`selectionMode="range"`).
   * Passes `[start, end]` (end may equal start for a one-day range) or `null` when cleared.
   */
  onDateRangeChange?: (range: [Date, Date] | null) => void;
  /** Whether the picker should show past or future dates. Defaults to "future". */
  pastOrFuture?: 'past' | 'future';
  /** Placeholder label when no date is selected */
  placeholder?: string;
  /**
   * `responsive` (default): popover on md+, mobile sheet below.
   * `embedded`: inline calendar under the pill — use inside dialogs where a
   * nested mobile sheet cannot open above the parent modal.
   */
  presentation?: 'responsive' | 'embedded';
  /** Currently selected date (`selectionMode="single"`). */
  selectedDate?: Date | null;
  /**
   * Currently selected inclusive range (`selectionMode="range"`).
   * Pass `[start, end]` or `null` when cleared.
   */
  selectedDateRange?: [Date, Date] | null;
  /**
   * `single` (default) or `range` calendar selection. Range is used by the
   * venue upcoming-events dialog on mobile and desktop.
   */
  selectionMode?: DatePickerSelectionMode;
  /**
   * Stacking order for the mobile `DatePickerSheet`. Pass a value above
   * `modal` (e.g. `toast`) when the pill sits inside another dialog.
   */
  sheetZIndex?: string | number;
};

function pillShellBg(isDisabled: boolean | undefined, isActive: boolean): string {
  if (isDisabled) return 'fill.button.secondary.disabled';
  if (isActive) return 'fill.button.secondary.selected';
  return 'fill.button.secondary.enabled';
}

function pillShellColor(isDisabled: boolean | undefined, isActive: boolean): string {
  if (isDisabled) return 'text.disabled';
  if (isActive) return 'text.white';
  return 'text.primary';
}

const PillShell = ({
  isActive,
  isDisabled,
  children,
}: {
  children: ReactNode;
  isActive: boolean;
  isDisabled?: boolean;
}) => (
  <HStack
    display="inline-flex"
    alignItems="center"
    gap={0}
    h={8}
    rounded="full"
    fontSize="sm"
    fontWeight="medium"
    overflow="hidden"
    bg={pillShellBg(isDisabled, isActive)}
    color={pillShellColor(isDisabled, isActive)}
    opacity={isDisabled ? 0.6 : 1}
  >
    {children}
  </HStack>
);

interface PillTriggerProps extends StackProps {
  isDisabled?: boolean;
  isOpen: boolean;
  label: string;
  showChevron: boolean;
}

const PillTrigger = ({
  label,
  isOpen,
  showChevron,
  isDisabled,
  onClick,
  ...rest
}: PillTriggerProps) => (
  <HStack
    as="button"
    gap={1}
    h="100%"
    pl={3}
    pr={showChevron ? 3 : 2}
    alignItems="center"
    bg="transparent"
    color="inherit"
    cursor={isDisabled ? 'not-allowed' : 'pointer'}
    aria-disabled={isDisabled || undefined}
    tabIndex={isDisabled ? -1 : undefined}
    onClick={isDisabled ? undefined : onClick}
    _hover={isDisabled ? undefined : { bg: 'fill.button.secondary.hover' }}
    {...rest}
  >
    <Span textOverflow="ellipsis" overflow="hidden" whiteSpace="nowrap">
      {label}
    </Span>
    {showChevron && (
      <Box as="span" boxSize={5} display="inline-flex" alignItems="center">
        {isOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
      </Box>
    )}
  </HStack>
);

const PillClear = ({ onClear }: { onClear: () => void }) => (
  <Box
    as="button"
    display="inline-flex"
    alignItems="center"
    justifyContent="center"
    boxSize={7}
    mr={1}
    flexShrink={0}
    rounded="full"
    color="inherit"
    _hover={{ bg: 'fill.button.secondary.hover', color: 'fill.icon.interactive' }}
    _active={{ bg: 'fill.button.secondary.active', color: 'fill.icon.interactive' }}
    onClick={onClear}
    aria-label="Clear date"
  >
    <Close />
  </Box>
);

function formatRangeLabel(range: [Date, Date]): string {
  const [start, end] = range;
  if (isSameDay(start, end)) return format(start, 'MMM d');
  if (start.getFullYear() === end.getFullYear()) {
    return `${format(start, 'MMM d')} – ${format(end, 'MMM d')}`;
  }
  return `${format(start, 'MMM d, yyyy')} – ${format(end, 'MMM d, yyyy')}`;
}

function datesFromValues(values: DateValue[]): [Date, Date] | null {
  const start = values[0] ? dateValueToDate(values[0]) : null;
  const end = values[1] ? dateValueToDate(values[1]) : start;
  if (!start || !end) return null;
  return start.getTime() <= end.getTime() ? [start, end] : [end, start];
}

/** Cancel discards the draft; Apply is the only commit. Divider is full-bleed. */
function DateFilterActions({
  canApply,
  onApply,
  onCancel,
}: {
  canApply: boolean;
  onApply: () => void;
  onCancel: () => void;
}) {
  return (
    <>
      <Separator borderColor="stroke.divider.default" w="full" />
      <Flex justify="flex-end" gap="2" px="4" py="4" w="full">
        <Button variant="secondary" size="md" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="primary" size="md" disabled={!canApply} onClick={onApply}>
          Apply
        </Button>
      </Flex>
    </>
  );
}

const DateFilterCalendar = ({
  maxDate,
  minDate,
  onSelect,
  selectionMode,
  value,
}: {
  maxDate?: Date;
  minDate?: Date;
  onSelect: (values: DateValue[]) => void;
  selectionMode: DatePickerSelectionMode;
  value: DateValue[];
}) => (
  <DatePickerMenu
    selectionMode={selectionMode}
    showSelectionText={false}
    boxShadow="none"
    w="full"
    minW="unset"
    maxW="full"
    px="0"
    pb="0"
    pt="0"
    value={value}
    min={minDate ? dateToDateValue(minDate) : undefined}
    max={maxDate ? dateToDateValue(maxDate) : undefined}
    onValueChange={({ value: nextValue }) => onSelect(nextValue)}
  />
);

/**
 * Date filter trigger. On md+ opens a calendar popover; on mobile opens
 * `DatePickerSheet` (draft + Submit). Use `presentation="embedded"` inside
 * dialogs so the calendar expands inline instead of nesting another modal.
 */
export function DateFilterPill({
  selectedDate = null,
  selectedDateRange = null,
  onDateChange,
  onDateRangeChange,
  pastOrFuture = 'future',
  placeholder = 'Filter by date',
  disabled = false,
  presentation = 'responsive',
  selectionMode = 'single',
  sheetZIndex,
}: DateFilterPillProps) {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isEmbeddedOpen, setIsEmbeddedOpen] = useState(false);
  // Draft stays local until Apply. Dismiss and Cancel throw it away.
  const [draftValue, setDraftValue] = useState<DateValue[]>([]);
  const isRange = selectionMode === 'range';
  const hasValue = isRange ? !!selectedDateRange : !!selectedDate;
  let label = placeholder;
  if (isRange && selectedDateRange) {
    label = formatRangeLabel(selectedDateRange);
  } else if (!isRange && selectedDate) {
    label = format(selectedDate, 'MMM d, yyyy');
  }

  const calendarValue = useMemo((): DateValue[] => {
    if (isRange) {
      if (!selectedDateRange) return [];
      const [start, end] = selectedDateRange;
      // Collapse same-day so desktop Zag range can take a second click as the end.
      if (isSameDay(start, end)) return [dateToDateValue(start)];
      return [dateToDateValue(start), dateToDateValue(end)];
    }
    return selectedDate ? [dateToDateValue(selectedDate)] : [];
  }, [isRange, selectedDate, selectedDateRange]);

  // Mobile sheet commits on Submit, not after each tap — keep both endpoints so a
  // reopened one-day range is a complete selection.
  const sheetValue = useMemo((): DateValue[] => {
    if (isRange && selectedDateRange) {
      const [start, end] = selectedDateRange;
      return [dateToDateValue(start), dateToDateValue(end)];
    }
    return calendarValue;
  }, [calendarValue, isRange, selectedDateRange]);

  const handleClear = () => {
    if (isRange) onDateRangeChange?.(null);
    else onDateChange?.(null);
  };

  const commitValues = (values: DateValue[]) => {
    if (isRange) {
      onDateRangeChange?.(datesFromValues(values));
      return;
    }
    const next = values[0];
    onDateChange?.(next ? dateValueToDate(next) : null);
  };

  const { minDate, maxDate } = useMemo(() => {
    const todayStart = startOfDay(new Date());
    return pastOrFuture === 'future'
      ? { minDate: todayStart, maxDate: undefined }
      : { minDate: undefined, maxDate: todayStart };
  }, [pastOrFuture]);

  const canApply = draftValue.length > 0;

  const applyDraft = () => {
    if (!canApply) return;
    commitValues(draftValue);
    setIsPopoverOpen(false);
    setIsEmbeddedOpen(false);
  };

  const cancelDraft = () => {
    setIsPopoverOpen(false);
    setIsEmbeddedOpen(false);
  };

  useEffect(() => {
    if (!disabled) return;
    setIsPopoverOpen(false);
    setIsMobileOpen(false);
    setIsEmbeddedOpen(false);
  }, [disabled]);

  if (presentation === 'embedded') {
    return (
      <Box>
        <PillShell isActive={!disabled && (hasValue || isEmbeddedOpen)} isDisabled={disabled}>
          <PillTrigger
            label={label}
            isOpen={isEmbeddedOpen}
            showChevron={!hasValue}
            isDisabled={disabled}
            onClick={() => {
              if (disabled) return;
              if (!isEmbeddedOpen) setDraftValue(calendarValue);
              setIsEmbeddedOpen((open) => !open);
            }}
          />
          {!disabled && hasValue && (
            <PillClear
              onClear={() => {
                handleClear();
                setIsEmbeddedOpen(false);
              }}
            />
          )}
        </PillShell>
        {isEmbeddedOpen && !disabled && (
          <Box
            mt="3"
            borderWidth="1px"
            borderColor="stroke.divider.default"
            borderRadius="md"
            bg="fill.background.default"
            overflow="hidden"
          >
            <Box px="4" pt="2" pb="2">
              <DateFilterCalendar
                value={draftValue}
                minDate={minDate}
                maxDate={maxDate}
                selectionMode={selectionMode}
                onSelect={setDraftValue}
              />
            </Box>
            <DateFilterActions canApply={canApply} onApply={applyDraft} onCancel={cancelDraft} />
          </Box>
        )}
      </Box>
    );
  }

  return (
    <>
      <Box hideBelow="md">
        <PopoverRoot
          size="md"
          open={!disabled && isPopoverOpen}
          onOpenChange={({ open }) => {
            if (disabled) return;
            if (open) setDraftValue(calendarValue);
            setIsPopoverOpen(!!open);
          }}
          positioning={{
            placement: 'bottom-end',
            gutter: 8,
            overflowPadding: 16,
          }}
          lazyMount
          unmountOnExit
        >
          <PillShell isActive={!disabled && (hasValue || isPopoverOpen)} isDisabled={disabled}>
            <PopoverTrigger asChild>
              <PillTrigger
                label={label}
                isOpen={isPopoverOpen}
                showChevron={!hasValue}
                isDisabled={disabled}
              />
            </PopoverTrigger>
            {!disabled && hasValue && <PillClear onClear={handleClear} />}
          </PillShell>
          <PopoverContent
            width="360px"
            maxWidth="calc(100vw - 32px)"
            p="0"
            bg="fill.background.default"
            borderWidth="0"
            borderRadius="md"
            boxShadow="0px 6px 12px rgba(0, 0, 0, 0.06)"
            overflow="hidden"
          >
            <PopoverBody p="0">
              <Box px="4" pt="2" pb="2">
                <DateFilterCalendar
                  value={draftValue}
                  minDate={minDate}
                  maxDate={maxDate}
                  selectionMode={selectionMode}
                  onSelect={setDraftValue}
                />
              </Box>
              <DateFilterActions canApply={canApply} onApply={applyDraft} onCancel={cancelDraft} />
            </PopoverBody>
          </PopoverContent>
        </PopoverRoot>
      </Box>

      <Box hideFrom="md">
        <PillShell isActive={!disabled && (hasValue || isMobileOpen)} isDisabled={disabled}>
          <PillTrigger
            label={label}
            isOpen={isMobileOpen}
            showChevron={!hasValue}
            isDisabled={disabled}
            onClick={() => {
              if (!disabled) setIsMobileOpen(true);
            }}
          />
          {!disabled && hasValue && <PillClear onClear={handleClear} />}
        </PillShell>
        <DatePickerSheet
          open={isMobileOpen}
          onOpenChange={setIsMobileOpen}
          onDismiss={() => setIsMobileOpen(false)}
          selectionMode={selectionMode}
          title={isRange ? 'Filter by dates' : 'Filter by date'}
          cancelLabel="Cancel"
          confirmLabel="Apply"
          value={sheetValue}
          min={minDate ? dateToDateValue(minDate) : undefined}
          max={maxDate ? dateToDateValue(maxDate) : undefined}
          zIndex={sheetZIndex}
          onValueChange={({ value }) => {
            commitValues(value);
          }}
        />
      </Box>
    </>
  );
}
